import React, {ReactNode, useCallback, useState} from 'react';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import ConfirmModal from 'shared/ui/Modal/ConfirmModal/ConfirmModal';
import EntityActionsMenu from 'shared/ui/MoreActions/EntityActionsMenu';

import {IAdminEntityModalProps} from '../AdminFormModal';

import styles from './AdminCrudRow.module.scss';

type DeleteResult = Promise<{data?: unknown, error?: unknown}>;

export type AdminDeleteMutationHook = () => readonly [(_id: number) => DeleteResult, {isLoading: boolean}];

export interface IAdminDeleteTexts {
    header: string,
    title: string,
    hint?: ReactNode,
    success: string,
}

interface IAdminCrudRowProps<T extends {id: number}> {
    item: T,
    name: string,
    EditModal: React.ComponentType<IAdminEntityModalProps<T>>,
    useDeleteMutation: AdminDeleteMutationHook,
    deleteTexts: IAdminDeleteTexts,
    extraActions?: ReactNode[],
    children: ReactNode,
}

type ModalType = 'edit' | 'delete' | null;

const AdminCrudRow = <T extends {id: number}>({
    item,
    name,
    EditModal,
    useDeleteMutation,
    deleteTexts,
    extraActions,
    children,
}: IAdminCrudRowProps<T>) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const [deleteItem, {isLoading}] = useDeleteMutation();

    const closeModal = useCallback(() => setActiveModal(null), []);

    const onConfirmDelete = useCallback(async() => {
        const result = await deleteItem(item.id);
        if ('error' in result) return;
        toast.info(deleteTexts.success);
        closeModal();
    }, [deleteItem, item.id, deleteTexts.success, closeModal]);

    return (
        <>
            <tr className={styles.row}>
                {children}
                <td>
                    <div>
                        <EntityActionsMenu
                            onEdit={() => setActiveModal('edit')}
                            onDelete={() => setActiveModal('delete')}
                            extraActions={extraActions}
                        />
                    </div>
                </td>
            </tr>
            <EditModal open={activeModal === 'edit'} handleClose={closeModal} item={item}/>
            <ConfirmModal
                variant="delete"
                open={activeModal === 'delete'}
                handleClose={closeModal}
                header={deleteTexts.header}
                title={deleteTexts.title}
                name={name}
                hint={deleteTexts.hint}
                confirmText={t`Удалить`}
                onConfirm={onConfirmDelete}
                isLoading={isLoading}
            />
        </>
    );
};

export default AdminCrudRow;
